import React, { useCallback, useEffect, useMemo } from "react";
import styles from "./TopBar.module.scss";
import { OrgSelect } from "../Filters/components/OrgSelect";
import { SvgV2 } from "@whatslly/ui-components/v2/BaseElements/SvgV2";
import type { MenuItemType } from "@whatslly/ui-components/UserMenu/UserMenu";
import { UserMenu } from "@whatslly/ui-components/UserMenu/UserMenu";
import type { UpdateUserBodyType } from "@whatslly/types/UpdateUserBody";
import { putJSON } from "@whatslly/commons/apiUtils";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { useDependencyInjector } from "@whatslly/commons/di";
import type { IUserStore } from "@whatslly/ui-components/services/UserStore";
import { InjectUserStore } from "@whatslly/ui-components/services/UserStore";
import { observer } from "mobx-react-lite";
import { getI18nLanguageFromUserSetting } from "@whatslly/commons/LanguageChangeUtils";
import type { IStorage } from "@whatslly/commons/storage.constants";
import { InjectStorage, STORAGE } from "@whatslly/commons/storage.constants";
import { toJS } from "mobx";
import dayjs from "dayjs";
import { AccountSelect } from "../Filters/components/AccountSelect";
import { NavItemType } from "@whatslly/ui-components/SideBarNavMenu";
import { modalService } from "@whatslly/ui-components/modals";
import { CreateSupportCaseModal } from "../../modals/CreateSupportCaseModal/CreateSupportCaseModal";
import type { IOrganizationStore } from "../../services/IOrganizationStore";
import { InjectOrganizationStore } from "../../services/IOrganizationStore";
import { UserPermissions } from "@whatslly/commons/granularPermissions";

export const TopBar: React.FC<{
  navigationOptionsWithSearchParams: NavItemType[];
}> = observer(({ navigationOptionsWithSearchParams }) => {
  const { t, i18n } = useTranslation();
  const userStore = useDependencyInjector<IUserStore>(InjectUserStore);
  const orgStore = useDependencyInjector<IOrganizationStore>(InjectOrganizationStore);
  const storage = useDependencyInjector<IStorage>(InjectStorage);
  const updateUser = useCallback(
    async (data: UpdateUserBodyType) => {
      const update = {
        ...data,
        name:
          data.firstName || data.lastName
            ? `${data.firstName ? `${data.firstName} ` : ""}${data.lastName ? data.lastName : ""}`
            : userStore.user?.name,
      };
      if (update.language) {
        i18n.changeLanguage(getI18nLanguageFromUserSetting(update.language));
      }
      const newUserData = Object.assign(toJS(userStore.user!), update);
      storage.set(STORAGE.USER, newUserData);
      userStore.updateUserData(newUserData);
      await putJSON({
        urlSuffix: `/integrations/users`,
        etoken: userStore.etoken ?? "",
        pod: await orgStore.wPod,
        body: JSON.stringify(data),
      });
    },
    [i18n, storage, userStore.etoken, orgStore.wPod, userStore.user]
  );

  useEffect(() => {
    if (!userStore.user) return;
    dayjs.locale(getI18nLanguageFromUserSetting(userStore.user.language));
  }, [userStore.user?.language, userStore.user]);

  const isAccountLayout = useMemo(() => {
    return navigationOptionsWithSearchParams.every((option) => option.to.includes("/account/"));
  }, [navigationOptionsWithSearchParams]);

  return (
    <div className={styles.topBar}>
      {userStore.hasAccessToMultipleOrgs ? (
        <>
          {!isAccountLayout ? (
            <OrgSelect showClear={userStore.isInternalUser} hideCreateOrg={!userStore.isAnyAdmin} hideChangeOrgName={!userStore.isAnyAdmin} />
          ) : (
            <AccountSelect />
          )}
        </>
      ) : (
        <Link to={userStore.user?.permissions === UserPermissions.none.toString() ? "" : "/home"} className={styles.logoLink}>
          <SvgV2 className={styles.logo} iconName={`tuvis`} width={40} height={40} />
        </Link>
      )}
      <div className={styles.topBarRightSide}>
        <UserMenu
          updateUser={updateUser}
          menuItems={
            [
              {
                testId: `refresh-user-button`,
                text: t("settings_refresh_user"),
                onClick: () => {
                  userStore.refreshUser(t);
                },
                separator: true,
              },
              userStore.user?.allowCreateSupportCase && {
                text: t("settings_support"),
                testId: `/navMenu-getsupport`,

                onClick: () => {
                  modalService.openModal(CreateSupportCaseModal);
                },
                separator: Boolean(userStore.user && !userStore.user.hide_logout),
              },
            ].filter(Boolean) as MenuItemType[]
          }
        />
      </div>
    </div>
  );
});
